import { useContext, useEffect, useRef, useState } from 'react';
import { jwtDecode } from 'jwt-decode';
import { Outlet } from 'react-router-dom';
import { useLocation } from 'react-router';
import { LoginContext } from '../../utils/context';
import { protectedProps } from './protected.props';
import API from '../../services/api-middleware';
import { LoaderOverlay } from '../../components';
import {
  applyUserConfigFromResponse,
  getUserConfigurations,
} from '../../modules/end-user/user/services';
import {
  redirectToUnauthenticatedLanding,
  getUnauthenticatedRedirectUrl,
} from '../unauthenticated-redirect';

const userConfigBaseUrl = process.env.REACT_APP_USER_SERVER_URL;

function hasNonEmptyModulePermissionsInStorage(): boolean {
  try {
    const raw = localStorage.getItem('permissions');
    if (!raw) return false;
    const arr = JSON.parse(raw) as unknown;
    return Array.isArray(arr) && arr.length > 0;
  } catch {
    return false;
  }
}

function getIsAuthenticatedFromStorage(): boolean {
  const token = localStorage.getItem('token');
  if (!token) return false;

  try {
    const decodedToken: { exp: number } = jwtDecode(JSON.parse(token));
    return (decodedToken?.exp ?? 0) * 1000 >= new Date().getTime();
  } catch {
    return false;
  }
}

/** Survives Protected remounts when navigating between layout routes. */
let permissionsHydrated =
  getIsAuthenticatedFromStorage() && hasNonEmptyModulePermissionsInStorage();

let ssoSetupHydrated = !new URLSearchParams(window.location.search).has('token');

function Protected({ children }: protectedProps) {
  const location = useLocation();
  const { setIsLoggedIn } = useContext(LoginContext);
  const [ssoSetupComplete, setSsoSetupComplete] = useState(ssoSetupHydrated);
  const [modulePermissionsReady, setModulePermissionsReady] = useState(permissionsHydrated);
  const ssoProcessStartedRef = useRef(false);

  // Check auth synchronously - no waiting needed
  const checkAuth = () => {
    const isAuthenticated = getIsAuthenticatedFromStorage();
    setIsLoggedIn(isAuthenticated);
    return isAuthenticated;
  };

  const isAuthenticated = checkAuth();

  // SSO: when token is in URL, load user configuration once (works for any module)
  const urlParams = new URLSearchParams(location.search);
  const hasSsoToken = urlParams.has('token');

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const hasToken = searchParams.has('token');
    if (!hasToken) {
      ssoSetupHydrated = true;
      setSsoSetupComplete(true);
      return;
    }
    if (ssoProcessStartedRef.current) return;
    ssoProcessStartedRef.current = true;

    const ssoToken = searchParams.get('token');
    if (!ssoToken) {
      ssoSetupHydrated = true;
      setSsoSetupComplete(true);
      return;
    }

    localStorage.setItem('token', JSON.stringify(ssoToken));
    const newUrl = window.location.pathname;
    window.history.replaceState({}, document.title, newUrl);

    const runSsoSetup = async () => {
      try {
        const decodedToken: { sub: string } = jwtDecode(ssoToken);
        const [accountId, userId] = decodedToken.sub.split('_');
        localStorage.setItem('user_id', userId);

        const response = await API(userConfigBaseUrl).get(`user/configuration?user_id=${userId}`, {
          headers: { Authorization: `Bearer ${ssoToken}` },
        });

        if (response.status === 200 && response.data?.data) {
          const { data } = response.data;

          applyUserConfigFromResponse(response);

          localStorage.setItem('team_name', JSON.stringify(data.team_name || ''));
          localStorage.setItem(
            'user_details',
            JSON.stringify({
              name: data.name || 'SSO User',
              role: data.role_name || 'User',
              team: data.team_name || '',
              roleId: data.role_id || 0,
              teamId: data.team_id || 0,
              account_id: parseInt(accountId, 10),
            }),
          );
          localStorage.setItem('email', JSON.stringify(data.email || ''));
          setIsLoggedIn(true);
        } else {
          redirectToUnauthenticatedLanding();
          return;
        }
      } catch {
        redirectToUnauthenticatedLanding();
        return;
      }
      ssoSetupHydrated = true;
      setSsoSetupComplete(true);
    };

    runSsoSetup();
  }, [location.search, setIsLoggedIn]);

  // Wait for module permissions: non-empty localStorage, or user/configuration fetch finished
  // (avoids child routes calling verifyAccess before permissions exist).
  useEffect(() => {
    if (!isAuthenticated) {
      setModulePermissionsReady(false);
      return undefined;
    }
    const searchParams = new URLSearchParams(location.search);
    if (searchParams.has('token') && !ssoSetupComplete) {
      return undefined;
    }

    let cancelled = false;

    const hydrateModulePermissions = async () => {
      if (permissionsHydrated || hasNonEmptyModulePermissionsInStorage()) {
        permissionsHydrated = true;
        if (!cancelled) setModulePermissionsReady(true);
        return;
      }

      try {
        const response = await getUserConfigurations();
        if (!cancelled && response?.status === 200 && response.data?.data) {
          applyUserConfigFromResponse(response);
        }
      } catch {
        // Network / auth errors: still unblock UI; verifyAccess may send user to /unauthorized
      } finally {
        permissionsHydrated = true;
        if (!cancelled) setModulePermissionsReady(true);
      }
    };

    hydrateModulePermissions().catch(() => {
      /* errors handled inside hydrateModulePermissions */
    });
    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, ssoSetupComplete]);

  if (!isAuthenticated && !hasSsoToken) {
    window.location.assign(getUnauthenticatedRedirectUrl());
    return null;
  }

  const isContentLoading =
    (hasSsoToken && !ssoSetupComplete) ||
    ((isAuthenticated || hasSsoToken) && !modulePermissionsReady);

  return (
    <div className="relative flex min-h-0 w-full flex-1 flex-col">
      {isContentLoading ? <LoaderOverlay scoped /> : (children ?? <Outlet />)}
    </div>
  );
}

export default Protected;
